'use client';
/**
 * Dossier d'intégration d'un nouveau recru (lot I2).
 *
 * Transcription vivante de `BIO24-F-PROC-01 v03` et de son enregistrement
 * `F-ENR-01` : l'accueil du jour J (14 actions), la pochette (6 pièces) et les
 * habilitations à acquérir. Ce n'est pas un plan qu'on imprime, c'est
 * l'enregistrement de ce qui a été fait.
 *
 * ── ⚠️ CE QUI A CHANGÉ LE 27/08/2026, ET POURQUOI ──────────────────────────
 *
 * L'écran transcrivait `INS.G1.01` (01/01/2025) : 4 phases par PROCESSUS —
 * pré-analytique, analytique, post-analytique — soit 51 activités. Deux
 * défauts, et le second est le vrai :
 *
 *  · la procédure était REMPLACÉE depuis le 17/06/2026 ; l'application
 *    annonçait un parcours que le laboratoire n'applique plus ;
 *  · **43 de ces 51 activités étaient des compétences déjà écrites en
 *    grille** — « Réponse téléphonique », « Création des dossiers sur
 *    BOXYLAB » — mais en plus pauvre : une case, sans niveau, sans tuteur,
 *    sans méthode, sans réévaluation. La même chose vivait à deux endroits, et
 *    c'était la version pauvre qui servait de plan de formation.
 *
 * Découper par processus, c'était décrire le métier de TECHNICIEN : douze
 * personnes sur vingt n'avaient donc pas de parcours. Le nouveau modèle est
 * celui de la procédure — un accueil universel, puis les habilitations que la
 * fonction appelle.
 *
 * Deux points de vigilance portés par l'écran :
 *  · cocher une activité de formation n'habilite PERSONNE. L'écran le dit là où
 *    la confusion est possible — sur les activités qui nomment un automate ;
 *  · les pièces d'identité et bancaires sont marquées, et leur consultation est
 *    journalisée côté serveur. L'écran prévient avant d'ouvrir, pour que
 *    personne ne clique en croyant que c'est sans trace.
 */
import React from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed, userName } from '@/lib/store';
import type { RhIntegration, RhPersonnel, RhPreEmbauche } from '@/lib/types';
import { nomDe } from '@/lib/nom';
/* ⚠️ `RattacherCompte` a QUITTÉ ce fichier le 13/09/2026 : il est désormais
   atteignable aussi depuis la fiche du personnel, et deux copies auraient
   divergé au premier avertissement ajouté. Une seule implémentation. */
import { RattacherCompte } from '@/components/RattacherCompte';
import { fmtDate, aujourdhuiIso } from '@/lib/meta';
import { I, card, inputStyle, ConfirmButton, RefISO, SelecteurPastilles, Selecteur, Modale as Fenetre } from '@/components/ui';
import { Modale, lbl } from '@/components/screens/RhAbsences';
import { OngletsDossier } from '@/components/Dossier';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { BlocPieces } from '@/components/PiecesDossier';
/**
 * LE BORDEREAU DE LA POCHETTE — `BIO24-F-ENR-01 §3`.
 *
 * ⚠️ On coche un ACTE DE REMISE, jamais une pièce. Trois des six — fiche de
 * poste, livret de parcours, grille d'habilitation — DÉPENDENT DE LA FONCTION
 * et sont générées par l'outil : les recopier dans le dossier les figerait, et
 * elles cesseraient d'être vraies au premier changement de grille sans que rien
 * ne le dise. Ce que le dossier atteste, c'est qu'elles ont été remises ce
 * jour-là — pas ce qu'elles disaient.
 */
function Pochette({ dossier, clos }: { dossier: RhIntegration; clos: boolean }) {
  const { rhPochetteReference, remettrePiecePochette, settings } = useGed();
  /* ⚠️ UN DOSSIER OUVERT SOUS L'ANCIENNE PROCÉDURE n'a pas de bordereau : le
     champ n'existait pas. Afficher six cases vides ferait croire à six pièces
     non remises, alors que personne n'en a jamais été chargé. Le dossier
     d'Anis Marzouki est dans ce cas — huit activités y sont cochées sous
     INS.G1.01, et elles restent lisibles telles quelles. */
  if (!dossier.pochette) {
    return (
      <div style={{ ...card, padding: '15px 18px', marginTop: 12 }}>
        <strong style={{ fontSize: 13.5, color: 'var(--text-strong)' }}>Pochette d’intégration</strong>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '5px 0 0', lineHeight: 1.55 }}>
          Ce dossier a été ouvert sous <strong>{dossier.versionProcedure || 'une procédure antérieure'}</strong>,
          qui ne prévoyait pas de bordereau de remise. Il n’y a donc rien à cocher ici —
          et rien qui manque. Les dossiers ouverts désormais suivent BIO24-F-ENR-01.
        </p>
      </div>
    );
  }
  const etat = new Map((dossier.pochette || []).map((p) => [p.cle, p]));
  const remises = (dossier.pochette || []).filter((p) => p.remise).length;
  const total = rhPochetteReference.length || (dossier.pochette || []).length;

  return (
    <div style={{ ...card, padding: 0, marginTop: 12, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px' }}>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>
          Pochette d’intégration
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-sm)', padding: '3px 9px',
          background: remises === total && total ? 'var(--green-50)' : 'var(--gray-25)',
          color: remises === total && total ? 'var(--green-600)' : 'var(--text-muted)' }}>
          {remises} / {total}
        </span>
      </div>
      {!rhPochetteReference.length ? (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, padding: '0 18px 16px' }}>
          Le bordereau n’a pas pu être lu : la liste ci-dessous serait fausse.
        </p>
      ) : rhPochetteReference.map((ref, i) => {
        const p = etat.get(ref.cle);
        return (
          <div key={ref.cle} style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '9px 18px', borderTop: '1px solid var(--border-subtle)' }}>
            <input type="checkbox" checked={!!p?.remise} disabled={clos}
              style={{ marginTop: 3, flex: 'none', cursor: clos ? 'not-allowed' : 'pointer' }}
              onChange={(e) => void remettrePiecePochette(dossier.id, ref.cle, e.target.checked)} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <span style={{ fontSize: 12.5, color: p?.remise ? 'var(--text-strong)' : 'var(--text-muted)', lineHeight: 1.5 }}>
                {ref.libelle}
              </span>
              <span style={{ marginLeft: 8, fontSize: 10.5, borderRadius: 'var(--radius-pill)', padding: '1px 7px',
                background: ref.origine === 'genere' ? 'var(--gray-100)' : 'var(--gray-25)', color: 'var(--text-muted)' }}>
                {ref.origine === 'genere' ? 'générée par l’outil' : ref.docCode}
              </span>
              {/* ⚠️ La réserve du livret de parcours : quatre profils sur cinq
                  n'existent sur papier pour personne. Le taire ferait croire
                  qu'on remet un document qui n'existe pas. */}
              {ref.reserve && (
                <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: '3px 0 0', lineHeight: 1.5 }}>
                  ⚠ {ref.reserve}
                </p>
              )}
              {p?.remise && p.le && (
                <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: '2px 0 0' }}>
                  remise le {p.le} par {userName(settings, p.par)}
                </p>
              )}
            </div>
            {i < 0 && <span />}
          </div>
        );
      })}
    </div>
  );
}

/**
 * LE PROGRAMME — les habilitations à acquérir pendant le parcours.
 *
 * ⚠️ La liste a été PROPOSÉE depuis la fonction à l'ouverture, et le RT
 * l'élague. `PR-RH-001` l'exige : « le calendrier prévisionnel personnalisé est
 * renseigné par le RT et le RQ avant l'arrivée ». Un technicien n'apprend pas
 * la métrologie, le stock et le management de la qualité en vingt-quatre
 * semaines, alors que les postes qui admettent sa fonction les exigent.
 *
 * ⚠️ Trois états, jamais deux : pas de dossier · dossier ouvert · prononcée.
 * Les confondre ferait relancer un tuteur qui a déjà commencé, ou croire
 * acquise une grille entièrement cochée mais non décidée.
 */
function ProgrammeHabilitations({ dossier, clos, onAllerHabilitations }: {
  dossier: RhIntegration; clos: boolean; onAllerHabilitations?: (orgId: string) => void;
}) {
  const { retenirHabilitations } = useGed();
  /* ⚠️ `naviguerVers` A DISPARU D'ICI (12/09/2026, volet ①). Il visait `rhhab`
     — l'écran sur lequel on se trouve déjà —, ce qui ne changeait rien : le
     bouton paraissait mort. On emprunte désormais le rappel de l'écran parent,
     qui bascule l'onglet ET pose la personne. */
  /* Même règle que pour la pochette : un dossier antérieur n'a pas de
     programme, et « 0 / 0 » se lirait comme une fonction sans habilitation
     alors que la question n'a jamais été posée. */
  if (!dossier.habilitations) {
    return (
      <div style={{ ...card, padding: '15px 18px', marginTop: 12 }}>
        <strong style={{ fontSize: 13.5, color: 'var(--text-strong)' }}>Habilitations à acquérir</strong>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '5px 0 0', lineHeight: 1.55 }}>
          Ce dossier est antérieur au rattachement du parcours aux habilitations :
          son programme n’a jamais été arrêté. Il se règle dans « Habilitations &amp; compétences ».
        </p>
      </div>
    );
  }
  const l = dossier.habilitations;
  const acquises = l.filter((h) => h.acquise).length;

  const retirer = async (code: string) => {
    await retenirHabilitations(dossier.id, l.filter((h) => h.code !== code).map((h) => h.code));
  };

  return (
    <div style={{ ...card, padding: 0, marginTop: 12, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px' }}>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>
          Habilitations à acquérir
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-sm)', padding: '3px 9px',
          background: l.length && acquises === l.length ? 'var(--green-50)' : 'var(--gray-25)',
          color: l.length && acquises === l.length ? 'var(--green-600)' : 'var(--text-muted)' }}>
          {acquises} / {l.length}
        </span>
      </div>
      {!l.length ? (
        /* ⚠️ AUCUNE habilitation n'est un RÉSULTAT, pas un retard. Le coursier
            et l'agent de nettoyage sont dans ce cas : leur parcours s'achève au
            jour J. L'ancien modèle les laissait à « 0 / 51 » indéfiniment. */
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, padding: '0 18px 16px', lineHeight: 1.55 }}>
          Cette fonction n’appelle aucune habilitation : le parcours s’achève au jour J,
          et il est complet. Ce n’est pas un retard.
        </p>
      ) : (
        <>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, padding: '0 18px 10px', lineHeight: 1.55 }}>
            Proposées d’après la fonction à l’ouverture du dossier. Le responsable technique
            <strong> retire ce qui ne s’apprend pas cette année</strong> — la procédure lui en donne la charge.
          </p>
          {l.map((h) => (
            <div key={h.code} style={{ display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 18px', borderTop: '1px solid var(--border-subtle)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>{h.libelle}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
                  {h.code}
                  {/* ⚠️ D'OÙ VIENT CETTE LIGNE. Dès qu'on peut ajouter à la
                      main, la liste porte deux choses très différentes : ce que
                      la FONCTION appelle, et ce que quelqu'un a DÉCIDÉ d'y
                      mettre. Sans la distinction, personne ne sait laquelle il
                      a le droit de retirer.
                      ⚠️ La phrase est « ajoutée au programme » et non « ajoutée
                      à la main » : `appelee: false` couvre aussi une fonction
                      qui a changé depuis l'ouverture, et l'écran ne sait pas
                      distinguer les deux — il ne doit donc pas prétendre le
                      faire. */}
                  {h.appelee === false && (
                    <span title="Cette ligne n’est pas appelée par la fonction actuelle de la personne : elle a été ajoutée au programme, ou la fonction a changé depuis l’ouverture du dossier."
                      style={{ marginInlineStart: 6, fontSize: 11, fontWeight: 700, color: 'var(--cyan-700)', cursor: 'help' }}>
                      ajoutée au programme
                    </span>
                  )}
                </div>
              </div>
              <span style={{ flex: 'none', fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)',
                padding: '2px 9px',
                background: h.acquise ? 'var(--green-50)' : h.dossier ? 'var(--amber-50)' : 'var(--gray-25)',
                color: h.acquise ? 'var(--green-600)' : h.dossier ? 'var(--amber-600)' : 'var(--text-faint)' }}>
                {h.acquise ? 'prononcée' : h.dossier ? 'dossier en cours' : 'pas de dossier'}
              </span>
              {!clos && (
                <Bouton taille="sm" onClick={() => void retirer(h.code)} title={`Retirer « ${h.libelle} » du programme de ce parcours`}>Retirer</Bouton>
              )}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: '10px 18px 16px' }}>
            {/* ⚠️ N'EXISTE QUE SI LE CHEMIN EXISTE. Absent le rappel, pas de
                bouton : un bouton qui ne mène nulle part est très exactement le
                défaut du volet ①, et le remplacer par un autre bouton mort
                n'aurait rien corrigé. */}
            {!!onAllerHabilitations && (
              <Bouton taille="sm" onClick={() => onAllerHabilitations(dossier.orgId)}>
                Ouvrir les dossiers d’habilitation
              </Bouton>
            )}
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              Le programme est <strong>arrêté à l’ouverture</strong> du dossier, depuis la fonction de
              la personne. Il ne se réécrit pas tout seul ensuite — un changement de fonction ne
              retire rien à un tuteur qui évalue.
            </span>
          </div>
        </>
      )}

      {/* ── AJOUTER UNE HABILITATION AU PROGRAMME (volet ②, 12/09/2026) ──────
          « Je ne peux pas rajouter des habilitations à acquérir non plus. »
          Le moteur savait le faire depuis le début : `retenirHabilitations`
          accepte la LISTE COMPLÈTE, et cet écran ne s'en servait que pour
          retirer. Il manquait l'interface, pas la route. */}
      {!clos && <AjoutAuProgramme dossier={dossier} />}
    </div>
  );
}

/**
 * Une ligne du dossier lu — libellé, valeur, et ce qu'on dit quand il n'y a
 * rien.
 *
 * ⚠️ DÉFINIE HORS DU COMPOSANT QUI L'EMPLOIE. Écrite à l'intérieur, c'est un
 * composant CRÉÉ À CHAQUE RENDU : le compilateur React le refuse, et à raison —
 * une identité qui change à chaque passage démonte et remonte le sous-arbre,
 * ce qui perd focus et sélection. Même règle qu'à la revue CQI (12/09/2026).
 *
 * ⚠️ « Non renseigné » plutôt qu'une case vide : un blanc se lit comme un
 * défaut d'affichage, et l'on va chercher la valeur ailleurs. Et l'absence a
 * parfois un SENS propre — « aucun badge » veut dire que la personne ne pointe
 * pas —, d'où `absent`.
 */
function LigneDossier({ label, valeur, absent }: {
  label: string; valeur?: string | null; absent?: string;
}) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderTop: '1px solid var(--border-subtle)' }}>
      <span style={{ flex: 'none', width: 190, fontSize: 12.5, color: 'var(--text-muted)' }}>{label}</span>
      {String(valeur || '').trim()
        ? <span style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>{valeur}</span>
        : <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{absent || 'non renseigné'}</span>}
    </div>
  );
}

/**
 * LE DOSSIER ADMINISTRATIF — volet ⑤ du chantier 009, en LECTURE.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── La demande, et l'arbitrage qu'elle appelait ────────────────────────────
 *
 * « Ajouter également toutes les données du dossier administratif. » — RQ,
 * 11/09/2026.
 *
 * Deux lectures possibles, et elles ne coûtent pas la même chose : RECOPIER ces
 * données dans le dossier d'intégration, ou les y AFFICHER depuis leur source.
 * **Arbitrage D2 de la RQ, 13/09/2026 : lire.**
 *
 * ⚠️ POURQUOI C'EST LE BON CHOIX, ET CE QU'IL ÉVITE. La date d'embauche existait
 * DÉJÀ deux fois — `preEmbauche.dateEmbauche` et `rh-personnel.dateEntree` — sans
 * que rien ne dise laquelle faisait foi. Un troisième formulaire en aurait fait
 * trois. Sur un SMQ accrédité, deux documents qui peuvent diverger sont une
 * non-conformité en attente : ce n'est pas une préférence d'architecture, c'est
 * un écart qu'on n'ouvre pas.
 *
 * ── Ce que ce bloc NE fait pas ─────────────────────────────────────────────
 *
 * ⚠️ IL N'ÉCRIT RIEN, et aucun champ n'y est éditable. Le jour où quelqu'un
 * ajoutera « juste un champ modifiable ici », la seconde porte d'écriture sera
 * rouverte et la divergence reprendra. Le contrôle statique le refuse.
 *
 * ⚠️ IL N'INVENTE AUCUNE RUBRIQUE. Ce qu'il montre est exactement ce que
 * `rh-personnel` et `rh-contrat` portent. Ce qui manquerait au laboratoire
 * reste à nommer — c'est la décision D4, encore ouverte.
 *
 * ⚠️ ET IL NE MONTRE NI RIB NI MATRICULE DE PAIE. Ce sont des données de
 * PAIEMENT : elles vivent sur la fiche, derrière le rôle qui en répond. Les
 * afficher dans un parcours d'intégration — que le tuteur et le RQ ouvrent —
 * les exposerait à des gens qui n'ont aucune raison de les lire.
 */
function DossierRhLu({ dossier }: { dossier: RhIntegration }) {
  const { rhPersonnel, rhContrats, naviguerVers } = useGed();
  const p = rhPersonnel.find((x) => x.orgId === dossier.orgId);
  const contrat = (rhContrats || []).find((c) => c.personnelOrgId === dossier.orgId && c.actif !== false);
  const pre = dossier.preEmbauche ?? {};

  /* ⚠️ UNE VALEUR RESTÉE DANS UN CHAMP DÉMÉNAGÉ SE SIGNALE. Les retirer du
     formulaire sans un mot ferait disparaître, de l'écran de celui qui les a
     saisies, une donnée que personne n'a décidé d'effacer. */
  const restes = CHAMPS_PRE_DEMENAGES
    .map((c) => ({ ...c, valeur: String(pre[c.cle] || '').trim() }))
    .filter((c) => c.valeur);

  return (
    <div style={{ ...card, padding: 18, marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, flex: 1, fontSize: 13, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.05em', color: 'var(--text-strong)' }}>
          Dossier administratif
        </h3>
        {/* ⚠️ « DOSSIERS RH » depuis le 06/10/2026, plus l'organigramme : celui-ci
            n'ouvre qu'un résumé en lecture. Et les deux appels sont FONDUS en un
            seul — `select` puis `naviguerVers` en deux temps laissait
            l'organigramme consommer la clé avant de se démonter. */}
        {!!p && (
          <Bouton taille="sm" onClick={() => naviguerVers('dossiers', { cle: 'selectedPersonne', valeur: p.orgId })}>
            Ouvrir le dossier pour modifier
          </Bouton>
        )}
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 0 12px', lineHeight: 1.55 }}>
        Lu sur la <strong>fiche de la personne</strong> et sur son <strong>contrat</strong> — ce parcours
        ne les recopie pas. Une même donnée écrite à deux endroits finit par en dire deux choses ;
        c’est là-bas qu’elle se corrige, et la correction se voit ici aussitôt.
      </p>

      {!p ? (
        /* Un vide qui dit POURQUOI il est vide — voir la règle des faux vides. */
        <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0 }}>
          La fiche de cette personne n’est pas chargée : rien ne peut être affirmé ici.
        </p>
      ) : (
        <>
          <LigneDossier label="N° de badge" valeur={p.badgeId} absent="aucun badge — la personne ne pointe pas" />
          <LigneDossier label="Date d’entrée" valeur={p.dateEntree ? fmtDate(p.dateEntree) : ''} />
          <LigneDossier label="Date de naissance" valeur={p.dateNaissance ? fmtDate(p.dateNaissance) : ''} />
          <LigneDossier label="Adresse e-mail" valeur={p.email} absent="aucune — cette personne ne recevra aucune diffusion" />
          <LigneDossier label="Téléphone" valeur={p.telephone} />
          <LigneDossier label="Site" valeur={p.site} />
          {/* ⚠️ LE CONTRAT EST UNE AUTRE SOURCE, et son absence a un SENS que
              l'écran doit dire : sans lui, ni congé ni heures supplémentaires ne
              se calculent. Un tiret laisserait croire à un détail. */}
          {contrat ? (
            <>
              <LigneDossier label="Contrat" valeur={contrat.contratType} />
              <LigneDossier label="Début de contrat" valeur={contrat.dateDebut ? fmtDate(contrat.dateDebut) : ''} />
              <LigneDossier label="Régime hebdomadaire" valeur={contrat.regimeHebdo ? `${contrat.regimeHebdo} h` : ''} />
              <LigneDossier label="Droit à congé" valeur={contrat.joursCongesAnnuels ? `${contrat.joursCongesAnnuels} jours` : ''} />
            </>
          ) : (
            <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderTop: '1px solid var(--border-subtle)' }}>
              <span style={{ flex: 'none', width: 190, fontSize: 12.5, color: 'var(--text-muted)' }}>Contrat</span>
              <span style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>
                Aucun contrat enregistré — sans lui, ni congé ni heures supplémentaires ne se calculent.
              </span>
            </div>
          )}
        </>
      )}

      {/* ⚠️ CE QUE LE FORMULAIRE NE DEMANDE PLUS, mais que ce dossier porte
          encore. Voir `CHAMPS_PRE_DEMENAGES`. */}
      {restes.length > 0 && (
        <div style={{ marginTop: 12, padding: '9px 12px', borderRadius: 'var(--radius-sm)',
          background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)',
          fontSize: 12, lineHeight: 1.55 }}>
          <strong>Saisi autrefois dans la pré-embauche, et conservé :</strong>{' '}
          {restes.map((c) => `${c.libelle} — ${c.valeur}`).join(' · ')}.
          <br />
          Ces rubriques ont quitté le formulaire : elles font désormais foi sur{' '}
          {restes.map((c) => c.ou).join(' et ')}. Rien n’est effacé ; vérifiez que les deux
          concordent, puis ne vous fiez qu’à la fiche.
        </div>
      )}
    </div>
  );
}

/**
 * LE COMPTE ET LA PLACE DANS L'ORGANIGRAMME — volets ⑥ ⑦ ⑧ du chantier 009.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Ajouter également la position dans l'organigramme. Ajouter aussi l'envoi
 *  d'un lien pour créer un compte bio24. Si la personne a déjà créé son compte,
 *  permettre de relier la personne en tant que membre de l'équipe à son
 *  compte. » — RQ, 11/09/2026.
 *
 * ── ⑥ L'ORGANIGRAMME SE LIT ICI, IL S'ÉCRIT AILLEURS ──────────────────────
 *
 * Rattachement, cases tenues, pilotages : tout cela vit sur la fiche de la
 * personne, et c'est là que ça s'édite. ⚠️ Deux endroits pour écrire
 * `fonctionIds` ont DÉJÀ divergé une fois — c'est écrit dans `Organigramme.tsx`.
 * On montre, et on donne le chemin.
 *
 * ── ⑦ ET ⑧ SONT DEUX GESTES OPPOSÉS, ET L'ÉCRAN DOIT LE DIRE ──────────────
 *
 * Inviter CRÉE un compte ; rattacher en REVENDIQUE un qui existe. Les présenter
 * côte à côte sans le dire ferait inviter quelqu'un qui a déjà un accès — et
 * l'on se retrouverait avec deux comptes pour une personne, dont un que
 * personne ne réclame.
 *
 * ⚠️ RATTACHER EST RÉSERVÉ AU RQ (arbitrage du 13/09/2026), et le moteur le
 * revérifie. Le geste donne les accès d'un compte à une personne nommée : le
 * journal chaîné attribuera ensuite ses actes à ce nom.
 */
function CompteEtOrganigramme({ dossier, clos }: { dossier: RhIntegration; clos: boolean }) {
  const { rhPersonnel, session, naviguerVers, showToast, loadRh } = useGed();
  const estRQ = session.roles.includes('rq');
  const personne = rhPersonnel.find((p) => p.orgId === dossier.orgId);
  const compte = String(personne?.username || '').trim();

  return (
    <div style={{ ...card, padding: 18, marginTop: 12, display: 'grid', gap: 16 }}>
      {/* ── ⑥ LA PLACE DANS L'ORGANIGRAMME — en lecture ───────────────────── */}
      <div>
        <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.05em', color: 'var(--text-strong)' }}>Place dans l’organigramme</h3>
        {!personne ? (
          /* ⚠️ Un vide qui dit POURQUOI : la fiche peut ne pas être chargée, et
             « aucune case » se lirait comme un fait. */
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
            La fiche de cette personne n’est pas chargée : rien ne peut être affirmé ici.
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 5, fontSize: 12.5 }}>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Rattaché(e) à </span>
              <strong>{rhPersonnel.find((x) => x.orgId === personne.responsableOrgId)
                ? nomDe(rhPersonnel.find((x) => x.orgId === personne.responsableOrgId))
                : 'la racine de l’organigramme'}</strong>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Cases tenues </span>
              {(personne.fonctionIds || []).length
                ? <strong>{(personne.fonctionIds || []).length} case(s)</strong>
                : <span style={{ color: 'var(--amber-600)' }}>aucune — la personne ne tient aucune case</span>}
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Figure à l’organigramme </span>
              <strong>{personne.dansOrganigramme === false ? 'non' : 'oui'}</strong>
            </div>
            {/* ⚠️ L'ÉDITION EST AILLEURS, et le bouton le dit. Rééditer ici
                donnerait deux portes pour écrire `fonctionIds`. */}
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <Bouton taille="sm" onClick={() => naviguerVers('dossiers', { cle: 'selectedPersonne', valeur: personne.orgId })}>
                Ouvrir le dossier pour modifier
              </Bouton>
            </div>
          </div>
        )}
      </div>

      <div style={{ height: 1, background: 'var(--border-default)' }} />

      {/* ── ⑦ ET ⑧ — LE COMPTE ──────────────────────────────────────────── */}
      <div>
        <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.05em', color: 'var(--text-strong)' }}>Compte de connexion</h3>
        {compte ? (
          <p style={{ fontSize: 12.5, margin: 0, lineHeight: 1.55 }}>
            Rattaché(e) au compte <strong style={{ fontFamily: 'var(--font-mono)' }}>{compte}</strong>
            {personne?.email ? <> · {personne.email}</> : null}.
            <br />
            <span style={{ color: 'var(--text-muted)' }}>
              Le profil, les accès et la réinitialisation se règlent dans Paramétrage → Utilisateurs.
            </span>
          </p>
        ) : (
          <>
            {/* ⚠️ « PAS DE COMPTE » N'EST PAS UN MANQUE. Plus de la moitié du
                personnel n'en a aucun : le coursier et l'agent d'entretien
                restent planifiables, habilitables et payés. Le dire évite qu'on
                en ouvre un « pour faire propre ». */}
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.55 }}>
              Cette personne <strong>n’a pas de compte</strong>, et ce n’est pas forcément un manque :
              plus de la moitié du personnel n’en a aucun. Elle reste planifiable, habilitable et payée —
              mais elle ne recevra aucune diffusion et n’accusera aucune lecture.
            </p>
            {!clos && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {/* ⑦ CRÉER — l'invitation part de l'écran des comptes, qui sait
                    déjà dire si le courriel n'est PAS parti (lot 1). Refaire un
                    bouton d'envoi ici rebâtirait ce verdict une seconde fois,
                    et la seconde finirait par oublier de le dire. */}
                <Bouton taille="sm" onClick={() => naviguerVers('rhparams')}>
                  Inviter — créer un compte
                </Bouton>
                {/* ⑧ REVENDIQUER un compte qui existe déjà. */}
                {estRQ && personne && (
                  <RattacherCompte personne={personne}
                    onFait={() => { showToast('Compte rattaché.'); void loadRh(); }} />
                )}
              </div>
            )}
            {!clos && !estRQ && (
              <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '8px 0 0', lineHeight: 1.5 }}>
                Rattacher un compte existant relève du <strong>responsable qualité</strong> : le geste donne
                les accès d’un compte à une personne nommée, et le journal attribuera ensuite ses actes
                à ce nom.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Les cinq temps d'un parcours, DANS L'ORDRE OÙ ILS ARRIVENT (chantier 009).
 *  ⚠️ Le rang est dans le libellé (« 1 · », « 2 · ») : sans lui, des onglets
 *  côte à côte se lisent comme des rubriques indépendantes, et rien ne dit
 *  qu'ils décrivent une SUITE. */
type OngletParcours = 'preembauche' | 'preparation' | 'jourj' | 'compte' | 'pieces' | 'cloture';

/**
 * AJOUTER UNE HABILITATION AU PROGRAMME — volet ② du chantier 009.
 *
 * ── Ce qui manquait (RQ, 11/09/2026) ───────────────────────────────────────
 *
 * « Je ne peux pas rajouter des habilitations à acquérir non plus. »
 *
 * Le moteur le savait depuis le début : `retenirHabilitations` accepte la
 * LISTE COMPLÈTE du programme, et cet écran ne s'en servait que pour RETIRER.
 * Il manquait l'interface, pas la route.
 *
 * ── Deux listes, et elles ne disent pas la même chose ──────────────────────
 *
 * ⚠️ « CE QUE LA FONCTION APPELLE » est proposé en premier, à part : ce sont
 * des lignes qui ont été retirées du programme, ou que la fonction a gagnées
 * depuis l'ouverture. Les remettre est un geste courant et sûr.
 *
 * ⚠️ « TOUT LE RÉFÉRENTIEL » vient ensuite, et c'est un autre geste : on ajoute
 * quelque chose que la fonction n'appelle pas. C'est légitime — un technicien
 * qu'on prépare à tenir un poste de plus —, mais ce n'est pas la même décision,
 * et la ligne portera « ajoutée au programme » pour qu'on s'en souvienne.
 *
 * ⚠️ ON ENVOIE TOUJOURS LA LISTE COMPLÈTE, jamais un delta : c'est ce que la
 * route attend, et c'est aussi ce qui rend l'écriture idempotente. Un delta
 * appliqué deux fois sur un clic répété produirait un programme différent.
 */
function AjoutAuProgramme({ dossier }: { dossier: RhIntegration }) {
  const { retenirHabilitations, rhHabilitations } = useGed();
  const [ouvert, setOuvert] = React.useState(false);
  const [envoi, setEnvoi] = React.useState(false);
  const auProgramme = (dossier.habilitations || []).map((h) => h.code);
  const appelables = dossier.habilitationsAppelables || [];

  /* Le référentiel ACTIF, moins ce qui est déjà au programme et moins ce que la
     fonction appelle — celles-là sont proposées au-dessus, et les répéter
     ferait choisir deux fois la même chose à deux endroits. */
  const autres = (rhHabilitations || [])
    .filter((h) => h.actif !== false)
    .filter((h) => !auProgramme.includes(h.code))
    .filter((h) => !appelables.some((a) => a.code === h.code))
    .map((h) => ({ documentId: h.code, code: h.code, nom: h.libelle }));

  const ajouter = async (codes: string[]) => {
    if (!codes.length) return;
    setEnvoi(true);
    try { await retenirHabilitations(dossier.id, [...auProgramme, ...codes]); }
    finally { setEnvoi(false); }
  };

  if (!ouvert) {
    return (
      <div style={{ padding: '0 18px 16px' }}>
        <Bouton taille="sm" onClick={() => setOuvert(true)}>
          <I n="plus" s={13} /> Ajouter une habilitation au programme
        </Bouton>
      </div>
    );
  }
  return (
    <div style={{ padding: '0 18px 16px', display: 'grid', gap: 10 }}>
      {appelables.length > 0 && (
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', marginBottom: 5 }}>
            Appelées par sa fonction, absentes du programme
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {appelables.map((h) => (
              <Bouton taille="sm" key={h.code} type="button" disabled={envoi} onClick={() => void ajouter([h.code])} title={`Remettre « ${h.libelle} » au programme`}>
                <I n="plus" s={12} />{h.libelle}
              </Bouton>
            ))}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.45 }}>
            Elles en ont été retirées, ou la fonction les a gagnées depuis l’ouverture du dossier.
          </div>
        </div>
      )}
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', marginBottom: 5 }}>
          Une autre habilitation du référentiel
        </div>
        <SelecteurPastilles mono={false} modifiable={!envoi} unite="habilitation"
          ariaLabel="Habilitation à ajouter au programme"
          placeholder="Choisir une habilitation…"
          vide="Aucune ajoutée depuis le référentiel."
          options={autres} choisis={[]}
          onChange={(codes) => void ajouter(codes)} />
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.45 }}>
          Ce que la fonction n’appelle pas — par exemple pour préparer quelqu’un à tenir un poste de
          plus. La ligne portera « ajoutée au programme ».
        </div>
      </div>
      <div>
        <Bouton taille="sm" onClick={() => setOuvert(false)} disabled={envoi}>Fermer</Bouton>
      </div>
    </div>
  );
}

export function DossierIntegration({ dossier, onFermer, onAllerHabilitations }: {
  dossier: RhIntegration;
  onFermer: () => void;
  /** Bascule vers l'onglet « Habilitations par personne » EN POSANT la
   *  personne. ⚠️ Absent, le bouton n'est pas affiché : un bouton qui ne mène
   *  nulle part est précisément le défaut du volet ① — mieux vaut pas de
   *  bouton qu'un bouton mort. */
  onAllerHabilitations?: (orgId: string) => void;
}) {
  const { cocherActivite, cloturerIntegration, deposerPiece, consulterPiece, supprimerPiece, session, rhAcces, settings } = useGed();
  const [ouverte, setOuverte] = React.useState<string | null>(dossier.phases[0]?.cle ?? null);
  const [cloture, setCloture] = React.useState(false);
  /* ── DES ONGLETS, ET DANS L'ORDRE CHRONOLOGIQUE (volets ③ et ④, 12/09/2026)
   *
   * « À la place des encadrés, je veux avoir des onglets pour chaque partie.
   *  Organiser l'ordre des onglets de façon chronologique. Préembauche pour
   *  administratif… » — RQ, 11/09.
   *
   * ⚠️ L'ORDRE ÉTAIT BIEN FAUX, et d'une façon précise : la PRÉ-EMBAUCHE — ce
   * qui se règle avant que la personne n'arrive — venait en QUATRIÈME position,
   * APRÈS les activités du jour J. On lisait donc le dossier à rebours de la
   * façon dont il se remplit.
   *
   * ⚠️ ET SIX BLOCS EMPILÉS FONT PLUSIEURS ÉCRANS DE HAUT — c'est le défaut du
   * lot 2, reproduit à l'intérieur d'un dossier : la chose existe, mais plus
   * bas que le regard. Les onglets ne sont pas un habillage, ils rendent
   * atteignable ce qui ne l'était qu'en défilant.
   *
   * L'ordre suit le TEMPS :
   *   avant l'arrivée → l'arrivée → au fil de l'eau → à la fin.
   *
   * ⚠️ LE COMPTEUR D'AVANCEMENT RESTE HORS ONGLETS (dans l'en-tête) : « 7 sur
   * 14 » est ce qu'on vient chercher d'un coup d'œil, et l'enfermer dans un
   * onglet obligerait à le déplier pour savoir s'il y a lieu de le déplier. */
  const [onglet, setOnglet] = React.useState<OngletParcours>('preembauche');
  const clos = dossier.statut === 'cloture';

  const total = dossier.phases.reduce((n, p) => n + p.activites.length, 0);
  const faites = dossier.phases.reduce((n, p) => n + p.activites.filter((a) => a.fait).length, 0);
  /* ⚠️ CE COMPTEUR DIT CE QUI EST FAIT, pas ce qui reste — contrairement à
     celui du jour J. Neuf champs administratifs dont zéro renseigné se lirait
     « 9 » sur l'onglet, comme s'il y avait neuf choses à voir : on affiche donc
     le nombre RENSEIGNÉ, et rien quand il est nul. */
  const renseignesPre = CHAMPS_PRE
    .filter((c) => String((dossier.preEmbauche ?? {})[c.cle] || '').trim()).length;

  return (
    <section>
      <Bouton taille="sm" onClick={onFermer} style={{ marginBottom: 14 }}>
        <I n="chevron-left" s={13} /> Retour aux parcours
      </Bouton>

      <div style={{ ...card, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: 'var(--text-strong)' }}>
              Dossier d’intégration — {dossier.nom}
            </h2>
            <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
              Débuté le {dossier.dateDebut || '—'} · {faites} activité(s) faite(s) sur {total}
              <br />
              Procédure applicable à l’ouverture : <strong>{dossier.versionProcedure}</strong>.
              Une révision ultérieure ne réécrit pas ce dossier. <RefISO code="6.2" />
            </p>
          </div>
          <span style={{
            flex: 'none', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-sm)', padding: '4px 10px',
            background: clos ? 'var(--gray-25)' : 'var(--green-50)', color: clos ? 'var(--text-muted)' : 'var(--green-600)',
          }}>
            {clos ? `Clôturé le ${dossier.clotureLe}` : 'En cours'}
          </span>
        </div>

        <BoutonPochette orgId={dossier.orgId} nom={dossier.nom} />

        {clos && dossier.conclusion && (
          <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--text-strong)',
            background: 'var(--gray-25)', borderRadius: 'var(--radius-md)', padding: '10px 14px', lineHeight: 1.55 }}>
            <strong>Conclusion</strong> — {dossier.conclusion}
            <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
              par {userName(settings, dossier.cloturePar)}
            </span>
          </p>
        )}
      </div>

      <div style={{ marginTop: 14 }}>
        <OngletsDossier id={`parcours-${dossier.id}`} actif={onglet} onChange={setOnglet} items={[
          /* ⚠️ L'ORDRE DE CE TABLEAU EST LA CHRONOLOGIE DU PARCOURS. Ne pas le
             réordonner « par importance » : c'est précisément ce qui avait mis
             la pré-embauche après le jour J. */
          { k: 'preembauche', label: '1 · Pré-embauche', n: renseignesPre || undefined },
          { k: 'preparation', label: '2 · Préparation', n: (dossier.habilitations || []).length || undefined },
          { k: 'jourj', label: '3 · Jour J', n: total - faites || undefined },
          /* ⚠️ APRÈS le jour J, et c'est chronologique : on ouvre un accès à
             quelqu'un qui est ARRIVÉ. Avant l'arrivée, un compte actif est une
             porte ouverte pour personne. */
          { k: 'compte', label: '4 · Compte & organigramme' },
          { k: 'pieces', label: '5 · Pièces', n: (dossier.pieces || []).length || undefined },
          { k: 'cloture', label: '6 · Clôture' },
        ]} />
      </div>

      {/* ── 1 · AVANT L'ARRIVÉE — l'administratif ─────────────────────────── */}
      {onglet === 'preembauche' && (
        <>
          <BlocPreEmbauche dossier={dossier} clos={clos} />
          {/* ⚠️ LE DOSSIER RH SE LIT ICI, IL S'ÉCRIT SUR LA FICHE (arbitrage D2
              de la RQ, 13/09/2026). « Ajouter toutes les données du dossier
              administratif » aurait pu vouloir dire les RECOPIER : ce sont
              matricule, RIB, contrat, date d'entrée — tous déjà portés par
              `rh-personnel` et `rh-contrat`.
              Une troisième copie aurait produit trois dates d'embauche qui
              divergent, et sur un SMQ accrédité deux documents qui peuvent
              diverger sont une non-conformité en attente. */}
          <DossierRhLu dossier={dossier} />
        </>
      )}

      {/* ── 2 · AVANT L'ARRIVÉE — ce qu'on prépare ───────────────────────────
          Le bordereau de remise (F-ENR-01 §3) et les habilitations à acquérir.
          La procédure le dit : « préparation de la pochette » et « calendrier
          prévisionnel personnalisé renseigné par le RT et le RQ avant
          l'arrivée ». */}
      {onglet === 'preparation' && (
        <>
          <Pochette dossier={dossier} clos={clos} />
          <ProgrammeHabilitations dossier={dossier} clos={clos} onAllerHabilitations={onAllerHabilitations} />
        </>
      )}

      {/* ── 3 · L'ARRIVÉE — les activités du jour J ──────────────────────── */}
      {onglet === 'jourj' && dossier.phases.map((ph) => {
        const n = ph.activites.filter((a) => a.fait).length;
        const est = ouverte === ph.cle;
        return (
          <div key={ph.cle} style={{ ...card, padding: 0, marginTop: 12, overflow: 'hidden' }}>
            <button onClick={() => setOuverte(est ? null : ph.cle)}
              style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
                cursor: 'pointer', padding: '15px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <I n={est ? 'chevron-bas' : 'chevron-right'} s={14} />
              <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>{ph.titre}</span>
              <span style={{ fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-sm)', padding: '3px 9px',
                background: n === ph.activites.length ? 'var(--green-50)' : 'var(--gray-25)',
                color: n === ph.activites.length ? 'var(--green-600)' : 'var(--text-muted)' }}>
                {n} / {ph.activites.length}
              </span>
            </button>

            {est && (
              <div style={{ padding: '0 18px 16px' }}>
                <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.55 }}>
                  <strong>Objectifs</strong> — {ph.objectifs.join(' · ')}
                  <br /><strong>Responsables</strong> — {ph.responsables.join(' · ')}
                </p>

                {ph.activites.map((a) => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0',
                    borderTop: '1px solid var(--border-subtle)' }}>
                    <input type="checkbox" checked={a.fait} disabled={clos}
                      style={{ marginTop: 3, flex: 'none', cursor: clos ? 'not-allowed' : 'pointer' }}
                      onChange={(e) => void cocherActivite(dossier.id, a.id, e.target.checked)} />
                    <div style={{ minWidth: 0 }}>
                      <span style={{ fontSize: 12.5, color: a.fait ? 'var(--text-strong)' : 'var(--text-muted)', lineHeight: 1.5 }}>
                        {a.texte}
                      </span>
                      {a.fait && (
                        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
                          fait le {a.date} par {userName(settings, a.par)}
                          {a.note ? ` — ${a.note}` : ''}
                        </span>
                      )}
                      {a.fait && !!(a.habilitationsSuggerees || []).length && (
                        <SuggestionsFormation
                          orgId={dossier.orgId} suggestions={a.habilitationsSuggerees || []} clos={clos} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* ── 4 · APRÈS L'ARRIVÉE — le compte et la place dans l'organigramme ── */}
      {onglet === 'compte' && <CompteEtOrganigramme dossier={dossier} clos={clos} />}

      {/* ── 5 · AU FIL DE L'EAU — les pièces du dossier ──────────────────── */}
      {onglet === 'pieces' && (
        <BlocPieces
          pieces={dossier.pieces} clos={clos}
          /* ⚠️ `modifiable` SUR LE RÔLE `rh`, et non sur l'accès à cet écran.
             Le parcours s'ouvre au RQ et au RT (`exigerHabilitation`) ; dépôt,
             lecture et retrait d'une pièce exigent `rh` côté moteur depuis le
             05/10/2026 (`exigerRh`). Les boutons étaient donc proposés à qui
             recevait un 403 à chaque clic — ce qui se lit comme une panne, pas
             comme un droit manquant. */
          modifiable={!!rhAcces?.acces}
          /* ⚠️ `dossier.orgId` ET NON `dossier.id` (08/10/2026) : la pièce se
             dépose au dossier de la PERSONNE, plus à son parcours. */
          onDeposer={(cle, libelle, f) => void deposerPiece(dossier.orgId, cle, libelle, f)}
          onConsulter={consulterPiece}
          onSupprimer={(id) => void supprimerPiece(id)} />
      )}

      {/* ── 6 · À LA FIN — la clôture ────────────────────────────────────── */}
      {onglet === 'cloture' && (
      <div style={{ ...card, padding: 18, marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          {clos
            ? 'Ce dossier est clôturé : les activités ne sont plus modifiables.'
            : faites === total
              ? 'Toutes les activités sont faites : le dossier peut être clôturé.'
              : `${total - faites} activité(s) restent ouvertes. Clôturer maintenant demandera une conclusion.`}
        </span>
        {clos ? (
          session.roles.includes('rq') && (
            <ConfirmButton label="Rouvrir" question="Rouvrir ce dossier clôturé ?"
              onConfirm={() => void cloturerIntegration(dossier.id, '', true)} />
          )
        ) : (
          <Bouton variante="primaire" onClick={() => setCloture(true)}>Clôturer le dossier</Bouton>
        )}
      </div>
      )}

      {cloture && (
        <DialogueCloture restantes={total - faites}
          onFermer={() => setCloture(false)}
          onValider={async (c) => { if (await cloturerIntegration(dossier.id, c)) setCloture(false); }} />
      )}
    </section>
  );
}

/* ─────────── La pochette d'accueil, éditée sur papier (GED-15 c) ────────────
 *
 * `GET /ged/rh/accueil/pochette/:orgId` était autorisée depuis sa livraison sans
 * qu'aucun écran ne l'ouvre — alors que l'écran des grilles la MENTIONNE déjà
 * (« s'impriment sur la pochette », `RhGrilles.tsx:499`). Une pochette dont on
 * parle sans pouvoir l'éditer, c'est un tuteur qui la refait à la main.
 *
 * Le geste est ici parce que c'est ici qu'on est quand quelqu'un arrive : le
 * dossier d'intégration est ouvert, les pièces sont sous les yeux, et la
 * pochette est exactement ce qu'on emporte à la paillasse pour la journée
 * d'accueil. La mettre dans un écran d'édition à part reviendrait à demander à
 * la personne qui accueille d'aller la chercher ailleurs.
 *
 * ⚠️ Le moteur rend du HTML, pas du JSON : on l'ouvre dans un onglet, où il
 * s'imprime. Le cookie de session voyage avec la requête — c'est pour cela que
 * `window.open` suffit et qu'il ne faut PAS reconstruire l'appel en fetch pour
 * le réinjecter, ce qui perdrait la pagination d'impression du navigateur.
 *
 * ⚠️ Elle n'écrit rien AU DOSSIER, mais elle n'est pas silencieuse : le moteur
 * inscrit `PochetteEditee` au journal chaîné. C'est voulu — la pochette liste le
 * nom, la date et le déposant de chaque pièce, y compris celles marquées donnée
 * personnelle, et savoir qui l'a éditée fait partie du cloisonnement. Le contenu
 * des pièces, lui, n'y figure pas et aucune route de contenu n'est ouverte.
 */
function BoutonPochette({ orgId, nom }: { orgId: string; nom: string }) {
  const [avertit, setAvertit] = React.useState(false);
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border-subtle)' }}>
        <span style={{ flex: 1, minWidth: 240, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
          La <strong>pochette d’accueil</strong> — page de garde pré-remplie, grilles du périmètre
          avec leur cartouche et leur version, inventaire des pièces au dossier. C’est l’édition
          papier de ce dossier, celle qu’on emporte à la paillasse le premier jour.
        </span>
        <Bouton taille="sm" onClick={() => setAvertit(true)}>
          <I n="doc" s={13} /> Éditer la pochette
        </Bouton>
      </div>

      {avertit && (
        <Modale titre="Éditer la pochette d’accueil" onFermer={() => setAvertit(false)}>
          <p style={{ fontSize: 12.5, color: 'var(--text-body)', margin: '0 0 12px', lineHeight: 1.6 }}>
            La pochette de <strong>{nom}</strong> s’ouvrira dans un onglet, prête à imprimer.
          </p>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.6 }}>
            Elle <strong>inventorie</strong> les pièces du dossier — nom du fichier, date, déposant —
            y compris celles marquées « donnée personnelle ». Elle n’en reproduit
            <strong> aucun contenu</strong> : ni la carte d’identité, ni la photo, ni le RIB.
          </p>
          <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0, lineHeight: 1.6 }}>
            <strong>Cette édition est enregistrée à votre nom</strong> dans le journal du laboratoire.
          </p>
          <PiedActions taille="sm" style={{ marginTop: 18 }} annuler={{ onClick: () => setAvertit(false) }} principale={{ libelle: "Ouvrir la pochette", onClick: () => {
                window.open(`/api/ged/ged/rh/accueil/pochette/${encodeURIComponent(orgId)}`, '_blank', 'noopener');
                setAvertit(false);
              } }} />
        </Modale>
      )}
    </>
  );
}

/**
 * Le chaînon entre « il a été formé » et « il est habilité ».
 *
 * L'activité NOMME un automate ; le bouton propose d'enregistrer la formation
 * correspondante au niveau « en formation ». Il n'habilite pas : c'est
 * précisément la distinction que le module défend partout. L'habilitation réelle
 * se donne après évaluation, ailleurs, par un geste délibéré.
 */
function SuggestionsFormation({ orgId, suggestions, clos }: {
  orgId: string; suggestions: { code: string; libelle: string }[]; clos: boolean;
}) {
  const { rhDetenues, accorderHabilitation } = useGed();
  const detenu = (code: string) => rhDetenues.find((d) => d.orgId === orgId && d.code === code);

  return (
    <div style={{ marginTop: 5 }}>
      <span style={{ display: 'block', fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.5 }}>
        Formation enregistrée — <strong>elle n’habilite pas</strong>. L’habilitation se donne après
        évaluation, dans l’onglet « Par personne ».
      </span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 5 }}>
        {suggestions.map((h) => {
          const d = detenu(h.code);
          if (d) {
            return (
              <span key={h.code} style={{ fontSize: 12.5, color: 'var(--text-muted)',
                background: 'var(--gray-25)', borderRadius: 'var(--radius-sm)', padding: '2px 8px' }}>
                {h.libelle} — {d.niveau}
              </span>
            );
          }
          return (
            <Bouton taille="sm" key={h.code} disabled={clos} onClick={() => void accorderHabilitation({
                personnelOrgId: orgId, code: h.code, niveau: 'formation', origine: 'evaluation',
                dateObtention: aujourdhuiIso(),
                note: 'Formation suivie au titre du parcours d’intégration (INS.G1.01).',
              } as never)}>
              + Formation sur {h.libelle}
            </Bouton>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------------------- la pré-embauche ------------------------------ */

/** Les neuf champs, dans l'ordre de la page de garde de la pochette (N7-11/12). */
/**
 * ⚠️ CE QUI N'A DE SENS QU'AVANT L'EMBAUCHE — et rien d'autre (13/09/2026).
 *
 * ── L'arbitrage D2 de la RQ (13/09/2026) : « lire le dossier RH » ──────────
 *
 * Le formulaire portait aussi `dateEmbauche` et `debutContrat`. Or la date
 * d'entrée vit DÉJÀ sur `rh-personnel.dateEntree`, et le début de contrat sur
 * `rh-contrat.dateDebut` : **la même date existait à deux endroits**, et rien
 * ne disait lequel faisait foi.
 *
 * ⚠️ SUR UN SMQ ACCRÉDITÉ, DEUX DOCUMENTS QUI PEUVENT DIVERGER SONT UNE
 * NON-CONFORMITÉ EN ATTENTE. La pochette imprimée le savait déjà d'ailleurs :
 * elle lisait `p.dateEntree` EN PREMIER et ne retombait sur `pre.dateEmbauche`
 * qu'à défaut. La fiche RH était donc déjà la source de fait ; le champ de
 * pré-embauche n'était qu'un doublon qui attendait de diverger.
 *
 * Les deux sortent donc du FORMULAIRE. Ils restent acceptés par le moteur et
 * lisibles : aucune donnée n'est détruite, et le repli de la pochette continue
 * de fonctionner pour les dossiers anciens. Ce qu'on retire, c'est la seconde
 * PORTE D'ÉCRITURE — c'est elle qui fabriquait la divergence.
 *
 * ⚠️ Mesuré avant de trancher : sur le seul dossier ouvert en base, aucun des
 * deux champs n'était renseigné. La divergence n'avait pas encore commencé.
 */
const CHAMPS_PRE: { cle: keyof RhPreEmbauche; libelle: string; type?: 'date' | 'texte'; large?: boolean }[] = [
  { cle: 'fonctionVisee', libelle: 'Fonction visée' },
  { cle: 'numeroDossier', libelle: 'N° de dossier personnel' },
  { cle: 'qualification', libelle: 'Qualification' },
  { cle: 'diplome', libelle: 'Diplôme' },
  { cle: 'experience', libelle: 'Expérience' },
  { cle: 'tuteur', libelle: 'Tuteur désigné' },
  { cle: 'observations', libelle: 'Observations', large: true },
];

/** Les deux champs SORTIS du formulaire — le dossier RH les porte. Gardés ici
 *  pour les SIGNALER quand un dossier ancien en porte encore une valeur : les
 *  faire disparaître de l'écran sans un mot serait effacer une donnée aux yeux
 *  de celui qui l'a saisie. */
const CHAMPS_PRE_DEMENAGES: { cle: keyof RhPreEmbauche; libelle: string; ou: string }[] = [
  { cle: 'dateEmbauche', libelle: 'Date d’embauche', ou: 'la date d’entrée de la fiche RH' },
  { cle: 'debutContrat', libelle: 'Début de contrat', ou: 'le contrat de la personne' },
];

/**
 * Renseignements de pré-embauche — la page de garde de la pochette d'accueil.
 *
 * Le moteur savait les ÉCRIRE depuis le lot I3 (`POST
 * /ged/rh/accueil/pre-embauche`) et la pochette savait les IMPRIMER, mais
 * `GET ged/rh/integrations` ne les RENDAIT pas : aucun écran ne pouvait donc
 * les saisir sans risquer d'écraser ce que la base portait déjà. La lecture a
 * été ajoutée au moteur le 07/08/2026 ; ce bloc est le geste qui s'en sert.
 *
 * ⚠️ Rien de sensible ici, et c'est ce qui justifie un simple formulaire dans
 * le dossier : identité, photo et RIB continuent de passer par la check-list
 * des pièces, dont chaque consultation est journalisée.
 */
function BlocPreEmbauche({ dossier, clos }: { dossier: RhIntegration; clos: boolean }) {
  const { showToast, loadRh } = useGed();
  const enBase = React.useMemo(() => dossier.preEmbauche ?? {}, [dossier.preEmbauche]);
  const [f, setF] = React.useState<RhPreEmbauche>(enBase);
  const [edite, setEdite] = React.useState(false);
  const [envoi, setEnvoi] = React.useState(false);
  const renseignes = CHAMPS_PRE.filter((c) => String(enBase[c.cle] || '').trim());

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      const r = await fetchEcriture('/api/ged/ged/rh/accueil/pre-embauche', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personnelOrgId: dossier.orgId, preEmbauche: f }),
      });
      const d = await r.json().catch(() => null);
      if (!r.ok) { showToast(d?.error?.message || 'Enregistrement refusé.', 'erreur'); return; }
      showToast('Pré-embauche enregistrée — la pochette d’accueil s’en servira.');
      setEdite(false);
      await loadRh();
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div style={{ ...card, padding: 18, marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, flex: 1, fontSize: 13, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.05em', color: 'var(--text-strong)' }}>
          Pré-embauche
        </h3>
        {!clos && (
          <Bouton taille="sm" onClick={() => { setF(enBase); setEdite(!edite); }}>
            <I n={edite ? 'x' : 'pen'} s={13} /> {edite ? 'Annuler' : 'Renseigner'}
          </Bouton>
        )}
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 0 12px', lineHeight: 1.55 }}>
        Renseignements <strong>administratifs</strong> repris sur la page de garde de la pochette
        d’accueil. Ce qui n’est pas renseigné y reste une <strong>case vide</strong> — jamais une
        valeur inventée.
      </p>

      {!edite && renseignes.length === 0 && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
          Rien de renseigné : la page de garde de la pochette sortira avec ses cases à remplir
          à la main.
        </p>
      )}

      {!edite && renseignes.map((c) => (
        <div key={c.cle} style={{ display: 'flex', gap: 12, padding: '7px 0', borderTop: '1px solid var(--border-subtle)' }}>
          <span style={{ flex: 'none', width: 190, fontSize: 12.5, color: 'var(--text-muted)' }}>{c.libelle}</span>
          <span style={{ fontSize: 12.5, color: 'var(--text-strong)' }}>{String(enBase[c.cle])}</span>
        </div>
      ))}

      {edite && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12 }}>
            {CHAMPS_PRE.filter((c) => !c.large).map((c) => (
              <div key={c.cle}>
                <label style={lbl} htmlFor={`pe-${c.cle}`}>{c.libelle}</label>
                <input id={`pe-${c.cle}`} type={c.type === 'date' ? 'date' : 'text'}
                  value={String(f[c.cle] || '')} style={{ ...inputStyle, width: '100%' }}
                  onChange={(e) => setF({ ...f, [c.cle]: e.target.value })} />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={lbl} htmlFor="pe-observations">Observations</label>
            <textarea id="pe-observations" value={String(f.observations || '')} rows={2}
              style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
              onChange={(e) => setF({ ...f, observations: e.target.value })} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
            <Bouton variante="primaire" disabled={envoi} onClick={() => void enregistrer()}>
              {envoi ? '…' : 'Enregistrer la pré-embauche'}
            </Bouton>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------- les pièces -------------------------------- */

/* ⚠️ `BlocPieces` ET `PIECES_ATTENDUES` ONT QUITTÉ CE FICHIER le 06/10/2026
   pour `@/components/PiecesDossier` : le dossier RH les emploie aussi, et une
   copie aurait fini par ne plus dire que la consultation est tracée — le schéma
   exact des deux « jumeaux » des 12 et 13/09. Même modèle que
   `RattacherCompte`. Le dépôt n'est plus bloqué par la clôture : voir l'en-tête
   du composant. */
function DialogueCloture({ restantes, onFermer, onValider }: {
  restantes: number; onFermer: () => void; onValider: (conclusion: string) => void;
}) {
  const [c, setC] = React.useState('');
  const exige = restantes > 0;
  return (
    <Modale titre="Clôturer le dossier d’intégration" onFermer={onFermer}>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.55 }}>
        {exige
          ? <><strong>{restantes} activité(s) ne sont pas faites.</strong> Clôturer reste possible —
              quelqu’un peut partir avant la fin du parcours — mais la conclusion devient
              obligatoire : un parcours interrompu sans explication ne se justifie pas en audit.</>
          : <>Toutes les activités sont faites. La conclusion reste utile pour consigner
              l’appréciation portée à l’issue du parcours.</>}
      </p>
      <label style={lbl}>Conclusion
        <textarea style={{ ...inputStyle, width: '100%', minHeight: 90 }} value={c} autoFocus
          onChange={(e) => setC(e.target.value)}
          placeholder={exige ? 'Motif de la clôture anticipée…' : 'Appréciation à l’issue du parcours…'} />
      </label>
      <PiedActions taille="sm" style={{ marginTop: 18 }} annuler={{ onClick: onFermer }} principale={{ libelle: "Clôturer", disabled: exige && !c.trim(), onClick: () => onValider(c) }} />
    </Modale>
  );
}
